import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changeTenantPlan,
  fetchTenantDetail,
  fetchTenantFeatures,
  fetchTenantModules,
  fetchTenants,
  fetchTenantUsage,
  overrideTenantFeature,
} from '../lib/tenantAdminApi';
import { filterTenants } from '../lib/tenantAdminFormatters';
import { tenantAdminQueryKeys } from '../lib/tenantAdminQueryKeys';

export const useTenantAdminData = () => {
  const queryClient = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [filters, setFilters] = useState({ search: '', plan: '', status: '' });
  const [activeTab, setActiveTab] = useState('overview');

  const tenantsQuery = useQuery({
    queryKey: tenantAdminQueryKeys.tenants,
    queryFn: fetchTenants,
    refetchInterval: 30000,
  });

  const tenants = useMemo(() => tenantsQuery.data?.items || [], [tenantsQuery.data?.items]);
  const filteredTenants = useMemo(() => filterTenants(tenants, filters), [tenants, filters]);

  const effectiveTenantId = selectedTenantId || filteredTenants?.[0]?.tenantId || null;

  const detailQuery = useQuery({
    queryKey: tenantAdminQueryKeys.tenantDetail(effectiveTenantId),
    queryFn: () => fetchTenantDetail(effectiveTenantId),
    enabled: Boolean(effectiveTenantId),
    refetchInterval: 30000,
  });

  const featuresQuery = useQuery({
    queryKey: tenantAdminQueryKeys.tenantFeatures(effectiveTenantId),
    queryFn: () => fetchTenantFeatures(effectiveTenantId),
    enabled: Boolean(effectiveTenantId),
    refetchInterval: 30000,
  });

  const modulesQuery = useQuery({
    queryKey: tenantAdminQueryKeys.tenantModules(effectiveTenantId),
    queryFn: () => fetchTenantModules(effectiveTenantId),
    enabled: Boolean(effectiveTenantId),
    refetchInterval: 30000,
  });

  const usageQuery = useQuery({
    queryKey: tenantAdminQueryKeys.tenantUsage(effectiveTenantId),
    queryFn: () => fetchTenantUsage(effectiveTenantId),
    enabled: Boolean(effectiveTenantId),
    refetchInterval: 20000,
  });

  const changePlanMutation = useMutation({
    mutationFn: ({ tenantId, plan }) => changeTenantPlan(tenantId, plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantAdminQueryKeys.tenants });
      if (effectiveTenantId) {
        queryClient.invalidateQueries({ queryKey: tenantAdminQueryKeys.tenantDetail(effectiveTenantId) });
        queryClient.invalidateQueries({ queryKey: tenantAdminQueryKeys.tenantFeatures(effectiveTenantId) });
        queryClient.invalidateQueries({ queryKey: tenantAdminQueryKeys.tenantModules(effectiveTenantId) });
      }
    },
  });

  const overrideFeatureMutation = useMutation({
    mutationFn: ({ tenantId, featureKey, enabled }) => overrideTenantFeature(tenantId, featureKey, enabled),
    onSuccess: () => {
      if (effectiveTenantId) {
        queryClient.invalidateQueries({ queryKey: tenantAdminQueryKeys.tenantFeatures(effectiveTenantId) });
        queryClient.invalidateQueries({ queryKey: tenantAdminQueryKeys.tenantModules(effectiveTenantId) });
      }
    },
  });

  const isLoading = tenantsQuery.isLoading || detailQuery.isLoading || featuresQuery.isLoading || usageQuery.isLoading || modulesQuery.isLoading;
  const error = tenantsQuery.error || detailQuery.error || featuresQuery.error || usageQuery.error || modulesQuery.error || changePlanMutation.error || overrideFeatureMutation.error;

  const refreshAll = () => {
    tenantsQuery.refetch();
    detailQuery.refetch();
    featuresQuery.refetch();
    modulesQuery.refetch();
    usageQuery.refetch();
  };

  return {
    tenants,
    filteredTenants,
    selectedTenantId: effectiveTenantId,
    setSelectedTenantId,
    filters,
    setFilters,
    activeTab,
    setActiveTab,
    tenantDetail: detailQuery.data || null,
    tenantFeatures: featuresQuery.data?.items || [],
    tenantModules: modulesQuery.data?.items || [],
    tenantUsage: usageQuery.data || null,
    changePlan: changePlanMutation.mutateAsync,
    overrideFeature: overrideFeatureMutation.mutateAsync,
    changingPlan: changePlanMutation.isPending,
    changingFeature: overrideFeatureMutation.isPending,
    refreshAll,
    isLoading,
    error,
  };
};

export default useTenantAdminData;
